<template>
  <el-card>
    <div class="toolbar">
      <el-input v-model="keyword" placeholder="昵称 / openid / 手机" clearable style="width: 260px" @keyup.enter.native="load" />
      <el-button type="primary" @click="load">查询</el-button>
      <el-button type="success" @click="openCreateUser">新增用户</el-button>
      <el-button type="warning" plain @click="openDefaultModules">设置默认权限</el-button>
    </div>
    <el-table :data="rows" border stripe v-loading="loading" style="width: 100%; margin-top: 12px">
      <el-table-column label="openid" min-width="160" show-overflow-tooltip>
        <template slot-scope="s">
          <el-tag v-if="s.row.pendingWechatBind" type="warning" size="mini">待微信登录</el-tag>
          <span v-else>{{ s.row.openid || '—' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="昵称" width="120" />
      <el-table-column prop="phone" label="手机" width="120" />
      <el-table-column label="首页权限" min-width="160">
        <template slot-scope="s">
          <span v-if="moduleCount(s.row) === 0" class="cell-empty">未分配</span>
          <el-tag v-else size="mini" type="success">{{ moduleCount(s.row) }} 项</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="测评截止" width="140">
        <template slot-scope="s">
          <span v-if="s.row.assessmentValidUntil">{{ formatUntilDisplay(s.row.assessmentValidUntil) }}</span>
          <span v-else-if="s.row.assessmentValidYear != null && s.row.assessmentValidMonth != null">
            {{ s.row.assessmentValidYear }} 年 {{ s.row.assessmentValidMonth }} 月（月底）
          </span>
          <span v-else class="cell-empty">—</span>
        </template>
      </el-table-column>
      <el-table-column label="当前可测评" width="110">
        <template slot-scope="s">
          <el-tag :type="rowAssessmentAllowed(s.row) ? 'success' : 'danger'">
            {{ rowAssessmentAllowed(s.row) ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="300" fixed="right">
        <template slot-scope="s">
          <el-button type="text" @click="openEdit(s.row)">设置有效期</el-button>
          <el-button type="text" @click="openUserModules(s.row)">分配权限</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      style="margin-top: 12px"
      background
      layout="total, prev, pager, next"
      :total="total"
      :page-size="size"
      :current-page.sync="page"
      @current-change="load"
    />

    <el-dialog title="新增小程序用户（预注册）" :visible.sync="createDlg" width="440px" :close-on-click-modal="false">
      <p class="hint">
        填写手机号与昵称后保存。用户首次<strong>微信一键登录</strong>且手机号一致时，将自动绑定 openid 并沿用此处昵称。
        测评有效期、首页权限请在列表中通过「设置有效期」「分配权限」单独配置。
      </p>
      <el-form label-width="88px">
        <el-form-item label="手机号" required>
          <el-input v-model="createForm.phone" placeholder="11 位手机号" maxlength="11" />
        </el-form-item>
        <el-form-item label="昵称" required>
          <el-input v-model="createForm.nickname" placeholder="小程序展示昵称" maxlength="32" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createDlg = false">取消</el-button>
        <el-button type="primary" :loading="createSaving" @click="saveCreateUser">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="设置测评有效期" :visible.sync="dlg" width="440px" :close-on-click-modal="false">
      <p class="hint">
        新注册用户默认不可使用测评。请选择<strong>截止日期</strong>（含当天仍可使用测评，<strong>次日</strong>起不可使用测评相关功能）。
        未设置或清除后，该用户不可测评。
      </p>
      <el-form label-width="100px">
        <el-form-item label="截止日期">
          <el-date-picker
            v-model="form.validUntil"
            type="date"
            placeholder="选择年月日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="clearValidity">取消</el-button>
        <el-button type="primary" @click="saveValidity">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="moduleDlgTitle"
      :visible.sync="moduleDlg"
      width="560px"
      :close-on-click-modal="false"
      @open="onModuleDlgOpen"
    >
      <p class="hint" v-if="moduleDlgMode === 'default'">
        勾选后，<strong>新注册</strong>的小程序用户将自动获得以下首页测评入口（已注册用户不受影响，需单独「分配权限」）。
      </p>
      <p class="hint" v-else>
        勾选后，该用户小程序首页仅展示对应测评/训练入口（须同时满足测评有效期）。
      </p>
      <div v-loading="moduleCatalogLoading" class="module-groups">
        <div class="module-group">
          <div class="module-group-title">表观测评</div>
          <el-checkbox-group v-model="selectedModules">
            <el-checkbox v-for="m in groupApparent" :key="m.code" :label="m.code">{{ m.label }}</el-checkbox>
          </el-checkbox-group>
        </div>
        <div class="module-group">
          <div class="module-group-title">功能测评</div>
          <el-checkbox-group v-model="selectedModules">
            <el-checkbox v-for="m in groupFunctional" :key="m.code" :label="m.code">{{ m.label }}</el-checkbox>
          </el-checkbox-group>
        </div>
        <div class="module-group">
          <div class="module-group-title">神经-认知增强</div>
          <el-checkbox-group v-model="selectedModules">
            <el-checkbox v-for="m in groupTraining" :key="m.code" :label="m.code">{{ m.label }}</el-checkbox>
          </el-checkbox-group>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="moduleDlg = false">取消</el-button>
        <el-button @click="selectAllModules">全选</el-button>
        <el-button @click="clearModuleSelection">清空</el-button>
        <el-button type="primary" :loading="moduleSaving" @click="saveModules">保存</el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import http from '../api/http';

const GROUP_APPARENT = ['emotion', 'cognition', 'adaption', 'achievement'];
const GROUP_FUNCTIONAL = ['hrcat', 'gradcpt', 'efcore', 'cdp'];
const GROUP_TRAINING = ['attention_train', 'memory_train', 'neuro_boost', 'mindfulness'];

export default {
  name: 'MiniUsersView',
  data() {
    return {
      loading: false,
      keyword: '',
      page: 1,
      size: 10,
      total: 0,
      rows: [],
      dlg: false,
      currentId: null,
      form: { validUntil: null },
      moduleCatalog: [],
      moduleCatalogLoading: false,
      moduleDlg: false,
      moduleDlgMode: 'user',
      moduleDlgTitle: '分配权限',
      moduleUserId: null,
      selectedModules: [],
      moduleSaving: false,
      createDlg: false,
      createSaving: false,
      createForm: {
        phone: '',
        nickname: ''
      }
    };
  },
  computed: {
    groupApparent() {
      return this.moduleCatalog.filter((m) => GROUP_APPARENT.includes(m.code));
    },
    groupFunctional() {
      return this.moduleCatalog.filter((m) => GROUP_FUNCTIONAL.includes(m.code));
    },
    groupTraining() {
      return this.moduleCatalog.filter((m) => GROUP_TRAINING.includes(m.code));
    }
  },
  mounted() {
    this.load();
    this.fetchCatalog();
  },
  methods: {
    moduleCount(row) {
      const list = row && row.homeModules;
      return Array.isArray(list) ? list.length : 0;
    },
    formatUntilDisplay(iso) {
      if (!iso || typeof iso !== 'string') return '—';
      const d = iso.indexOf('T') >= 0 ? iso.slice(0, 10) : iso;
      const p = d.split('-');
      if (p.length !== 3) return d;
      return `${p[0]}/${Number(p[1])}/${Number(p[2])}`;
    },
    rowAssessmentAllowed(row) {
      if (!row) return false;
      const untilStr = row.assessmentValidUntil;
      if (untilStr) {
        const d = typeof untilStr === 'string' && untilStr.indexOf('T') >= 0 ? untilStr.slice(0, 10) : untilStr;
        const parts = String(d).split('-');
        if (parts.length !== 3) return false;
        const y = Number(parts[0]);
        const m = Number(parts[1]);
        const day = Number(parts[2]);
        if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return false;
        const end = new Date(y, m - 1, day);
        const t = new Date();
        const today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
        return today.getTime() <= end.getTime();
      }
      if (row.assessmentValidYear == null || row.assessmentValidMonth == null) return false;
      const y = Number(row.assessmentValidYear);
      const m = Number(row.assessmentValidMonth);
      if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return false;
      const end = new Date(y, m, 0);
      const t = new Date();
      const today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
      return today.getTime() <= end.getTime();
    },
    async fetchCatalog() {
      this.moduleCatalogLoading = true;
      try {
        const { data } = await http.get('/admin/mini-users/module-catalog');
        const raw = data.data != null ? data.data : data;
        this.moduleCatalog = Array.isArray(raw) ? raw : [];
      } catch (e) {
        this.$message.error((e && e.message) || '加载模块列表失败');
      } finally {
        this.moduleCatalogLoading = false;
      }
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await http.get('/admin/mini-users', {
          params: { page: this.page, size: this.size, keyword: this.keyword || undefined }
        });
        const d = data.data || data;
        this.rows = d.records || [];
        this.total = d.total || 0;
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败');
      } finally {
        this.loading = false;
      }
    },
    openEdit(row) {
      const raw = row._id != null ? row._id : row.id;
      this.currentId = raw != null ? String(raw) : null;
      const u = row.assessmentValidUntil;
      this.form = {
        validUntil: u ? (typeof u === 'string' && u.indexOf('T') >= 0 ? u.slice(0, 10) : u) : null
      };
      this.dlg = true;
    },
    async saveValidity() {
      const u = this.form.validUntil;
      if (!u) {
        this.$message.warning('请选择截止日期；若需关闭测评权限，请点「清除有效期」');
        return;
      }
      try {
        await http.patch(`/admin/mini-users/${this.currentId}/assessment`, {
          validUntil: u
        });
        this.$message.success('已保存');
        this.dlg = false;
        this.load();
      } catch (e) {
        this.$message.error((e && e.message) || '保存失败');
      }
    },
    clearValidity() {
      this.dlg = false;
      this.load();
    },
    openCreateUser() {
      this.createForm = {
        phone: '',
        nickname: ''
      };
      this.createDlg = true;
    },
    async saveCreateUser() {
      const phone = (this.createForm.phone || '').trim();
      const nickname = (this.createForm.nickname || '').trim();
      if (!phone) {
        this.$message.warning('请填写手机号');
        return;
      }
      if (!/^1\d{10}$/.test(phone)) {
        this.$message.warning('请输入 11 位有效手机号');
        return;
      }
      if (!nickname) {
        this.$message.warning('请填写昵称');
        return;
      }
      this.createSaving = true;
      try {
        await http.post('/admin/mini-users', {
          phone,
          nickname
        });
        this.$message.success('预注册成功，用户微信登录后将自动绑定');
        this.createDlg = false;
        this.load();
      } catch (e) {
        this.$message.error((e && e.message) || '创建失败');
      } finally {
        this.createSaving = false;
      }
    },
    openDefaultModules() {
      this.moduleDlgMode = 'default';
      this.moduleDlgTitle = '设置默认权限（新注册用户）';
      this.moduleUserId = null;
      this.moduleDlg = true;
    },
    openUserModules(row) {
      const raw = row._id != null ? row._id : row.id;
      this.moduleDlgMode = 'user';
      this.moduleDlgTitle = `分配权限 · ${row.name || row.nickname || row.openid || ''}`;
      this.moduleUserId = raw != null ? String(raw) : null;
      this.selectedModules = Array.isArray(row.homeModules) ? [...row.homeModules] : [];
      this.moduleDlg = true;
    },
    async onModuleDlgOpen() {
      if (this.moduleDlgMode === 'default') {
        try {
          const { data } = await http.get('/admin/mini-users/default-modules');
          const raw = data.data != null ? data.data : data;
          this.selectedModules = Array.isArray(raw.modules) ? [...raw.modules] : [];
        } catch (e) {
          this.$message.error((e && e.message) || '加载默认权限失败');
          this.selectedModules = [];
        }
      }
    },
    selectAllModules() {
      this.selectedModules = this.moduleCatalog.map((m) => m.code);
    },
    clearModuleSelection() {
      this.selectedModules = [];
    },
    async saveModules() {
      this.moduleSaving = true;
      try {
        if (this.moduleDlgMode === 'default') {
          await http.put('/admin/mini-users/default-modules', {
            modules: this.selectedModules
          });
          this.$message.success('默认权限已保存，此后新注册用户将按此配置');
        } else {
          if (!this.moduleUserId) {
            this.$message.warning('用户无效');
            return;
          }
          await http.patch(`/admin/mini-users/${this.moduleUserId}/modules`, {
            modules: this.selectedModules
          });
          this.$message.success('用户权限已保存');
          this.load();
        }
        this.moduleDlg = false;
      } catch (e) {
        this.$message.error((e && e.message) || '保存失败');
      } finally {
        this.moduleSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.hint {
  font-size: 12px;
  color: #606266;
  margin-bottom: 12px;
  line-height: 1.5;
}
.cell-empty {
  color: #c0c4cc;
}
.module-groups {
  max-height: 420px;
  overflow-y: auto;
}
.module-group {
  margin-bottom: 16px;
}
.module-group-title {
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
  font-size: 14px;
}
.module-group .el-checkbox {
  display: block;
  margin: 6px 0;
}
</style>
